import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useOrganization, organizationQueryKey } from "@/queries/organization-queries";
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import {
    CreateGroupRequest,
    CreateMembershipRequest,
    DeleteGroupRequest,
    CreateRoleAssignmentRequest,
    DeleteMembershipRequest,
    DeleteRoleAssignmentRequest,
    ListMembershipsRequest,
    ShareResourceWithPrincipalRequest,
    UnshareResourceWithPrincipalRequest,
    UpdateGroupRequest,
    type Group,
    type GroupMembership,
    type ResourceRole,
    type RoleAssignment,
} from "gitpod-next-api/gitpod/v1/group_pb";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { type Principal, Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { autoPaginate, DEFAULT_PAGE_SIZE } from "@/queries/pagination";
import { computeResourcePermissions } from "@/utils/permissions";
import { hasResourceTypeChanges } from "@/utils/permission-diff";
import { useEffect, useRef } from "react";

export const groupsQueryKey = {
    list: (search?: string) => keyWithPrincipal(["groups", "list", "all", { search: search || "" }]),
    get: (groupId?: string) => keyWithPrincipal(["groups", "get", groupId]),
    memberships: (groupId?: string, pageSize?: number) =>
        keyWithPrincipal(["groups", "memberships", groupId, ...(pageSize != null ? [pageSize] : [])]),
    membershipsCursor: (groupId: string, token: string, search?: string) =>
        keyWithPrincipal(["groups", "memberships", groupId, "cursor", token, { search: search || "" }]),
    roleAssignments: (resourceType?: ResourceType, resourceId?: string, groupId?: string) =>
        keyWithPrincipal([
            "groups",
            "roleAssignments",
            // Omit undefined values so partialMatchKey can match broader prefixes
            // (e.g. { resourceType: PROJECT } matches { resourceType: PROJECT, groupId: "x" })
            Object.fromEntries(
                Object.entries({ resourceType, resourceId, groupId }).filter(([, v]) => v !== undefined),
            ),
        ]),
    userRoleAssignments: (userId?: string, resourceType?: ResourceType) =>
        keyWithPrincipal(["groups", "roleAssignments", "forUser", userId, resourceType]),
    userResourcePermissions: (resourceType?: ResourceType) =>
        keyWithPrincipal(["groups", "resourcePermissions", "forUser", resourceType]),
};

export type PlainGroup = PlainMessage<Group>;
export type PlainGroupMembership = PlainMessage<GroupMembership>;
export type PlainRoleAssignment = PlainMessage<RoleAssignment>;

export function toPlainGroup(group: Group): PlainGroup {
    return toPlainMessage(group);
}

export function toPlainGroupMembership(membership: GroupMembership): PlainGroupMembership {
    return toPlainMessage(membership);
}

export function toPlainRoleAssignment(assignment: RoleAssignment): PlainRoleAssignment {
    return toPlainMessage(assignment);
}

export const useListGroups = ({ search = "" }: { search?: string } = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: groupsQueryKey.list(search),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const groups = await autoPaginate(
                (params) => api.groupService.listGroups(params),
                search ? { filter: { search } } : {},
                (response) => response.groups,
            );

            return {
                groups: groups.map(toPlainGroup),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });
};

export type SubjectInput = {
    id: string;
    principal: Principal;
};

export type CreateGroupParams = {
    organizationId: string;
    name: string;
    description?: string;
    subjects?: SubjectInput[];
};

export const useCreateGroup = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ organizationId, name, description, subjects }: CreateGroupParams) => {
            const request = new CreateGroupRequest({ organizationId, name, description });
            const response = await api.groupService.createGroup(request);
            const newGroup = toPlainGroup(response.group!);

            // If subjects are provided, add members to the group
            if (subjects && subjects.length > 0) {
                const membershipPromises = subjects.map((subject) => {
                    const membershipRequest = new CreateMembershipRequest({
                        groupId: newGroup.id,
                        subject: new Subject({
                            id: subject.id,
                            principal: subject.principal,
                        }),
                    });
                    return api.groupService.createMembership(membershipRequest);
                });
                await Promise.all(membershipPromises);
            }

            return newGroup;
        },
        onSuccess: (newGroup, variables) => {
            // Invalidate the groups list to refetch and include the new group
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.list().slice(0, -1) });

            // Optionally set the new group in the cache immediately
            queryClient.setQueryData(groupsQueryKey.get(newGroup.id), newGroup);

            // If members were added, invalidate the memberships cache
            if (variables.subjects && variables.subjects.length > 0) {
                void queryClient.invalidateQueries({ queryKey: groupsQueryKey.memberships(newGroup.id) });
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type UpdateGroupParams = {
    groupId: string;
    name: string;
    description?: string;
};

export const useUpdateGroup = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ groupId, name, description }: UpdateGroupParams) => {
            const request = new UpdateGroupRequest({ groupId, name, description });
            const response = await api.groupService.updateGroup(request);
            return toPlainGroup(response.group!);
        },
        onSuccess: (updatedGroup) => {
            // Invalidate the groups list to refetch with updated data
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.list().slice(0, -1) });

            // Update the specific group in the cache
            queryClient.setQueryData(groupsQueryKey.get(updatedGroup.id), updatedGroup);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useDeleteGroup = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (groupId: string) => {
            const request = new DeleteGroupRequest({ groupId });
            const response = await api.groupService.deleteGroup(request);
            return response;
        },
        onSuccess: (_response, groupId) => {
            // Invalidate the groups list to refetch without the deleted group
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.list().slice(0, -1) });

            // Remove the specific group from the cache
            queryClient.removeQueries({ queryKey: groupsQueryKey.get(groupId) });

            // Remove the group's memberships from the cache
            queryClient.removeQueries({ queryKey: groupsQueryKey.memberships(groupId) });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type CreateGroupMembershipParams = {
    groupId: string;
    userId: string;
};

export const useCreateGroupMembership = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ groupId, userId }: CreateGroupMembershipParams) => {
            const request = new CreateMembershipRequest({
                groupId,
                subject: new Subject({ id: userId }),
            });
            const response = await api.groupService.createMembership(request);
            return toPlainGroupMembership(response.member!);
        },
        onSuccess: (_newMembership, { groupId }) => {
            // Invalidate the groups list to update member counts
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.list().slice(0, -1) });
            // Invalidate the specific group's memberships to show the new member
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.memberships(groupId) });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type AddGroupMembersParams = {
    groupId: string;
    subjects: SubjectInput[];
};

export const useAddGroupMembers = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ groupId, subjects }: AddGroupMembersParams) => {
            const promises = subjects.map((subject) => {
                const request = new CreateMembershipRequest({
                    groupId,
                    subject: new Subject({
                        id: subject.id,
                        principal: subject.principal,
                    }),
                });
                return api.groupService.createMembership(request);
            });
            const responses = await Promise.all(promises);
            return responses.map((response) => toPlainGroupMembership(response.member!));
        },
        onSuccess: (_newMemberships, { groupId }) => {
            // Invalidate the groups list to update member counts
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.list().slice(0, -1) });
            // Invalidate the specific group's memberships to show the new members
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.memberships(groupId) });
            // Invalidate listMembersInfinite queries so exclude_group_ids filtering reflects the new members
            void queryClient.invalidateQueries({
                queryKey: organizationQueryKey.listMembersInfinite("").slice(0, -1),
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useListGroupMemberships = (groupId: string, pageSize = 100) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();

    return useQuery({
        queryKey: groupsQueryKey.memberships(groupId, pageSize),
        queryFn: async () => {
            // Return empty list for non-Enterprise organizations
            if (organization?.tier !== OrganizationTier.ENTERPRISE) {
                return [];
            }

            const request = new ListMembershipsRequest({
                groupId,
                pagination: { pageSize },
            });
            const response = await api.groupService.listMemberships(request);
            return response.members.map(toPlainGroupMembership);
        },
        enabled: !!user && !!groupId && !!organization,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

const GROUP_MEMBERSHIPS_PAGE_SIZE = DEFAULT_PAGE_SIZE;

type GroupMembershipsCursorPageData = {
    members: PlainGroupMembership[];
    nextToken: string;
};

/**
 * Fetches a single page of group memberships using an opaque cursor token.
 * Pair with useCursorPagination() for prev/next navigation.
 */
export const useGroupMembershipsCursorPage = (groupId: string, token = "", search = "") => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();

    return useQuery({
        queryKey: groupsQueryKey.membershipsCursor(groupId, token, search),
        queryFn: async (): Promise<GroupMembershipsCursorPageData> => {
            if (organization?.tier !== OrganizationTier.ENTERPRISE) {
                return { members: [], nextToken: "" };
            }

            const request = new ListMembershipsRequest({
                groupId,
                pagination: { pageSize: GROUP_MEMBERSHIPS_PAGE_SIZE, token },
                filter: search ? { search } : undefined,
            });
            const response = await api.groupService.listMemberships(request);
            return {
                members: response.members.map(toPlainGroupMembership),
                nextToken: response.pagination?.nextToken ?? "",
            };
        },
        enabled: !!user && !!groupId && !!organization,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });
};

export type DeleteGroupMembershipParams = {
    membershipId: string;
    groupId: string;
};

export const useDeleteGroupMembership = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ membershipId }: DeleteGroupMembershipParams) => {
            const request = new DeleteMembershipRequest({
                membershipId,
            });
            const response = await api.groupService.deleteMembership(request);
            return response;
        },
        onSuccess: (_response, { groupId }) => {
            // Invalidate the groups list to update member counts
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.list().slice(0, -1) });
            // Invalidate the specific group's memberships to remove the deleted member
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.memberships(groupId) });
            // Invalidate listMembersInfinite queries so exclude_group_ids filtering reflects the removal
            void queryClient.invalidateQueries({
                queryKey: organizationQueryKey.listMembersInfinite("").slice(0, -1),
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

// Role Assignment hooks

export type ListRoleAssignmentsParams = {
    resourceType: ResourceType;
    resourceId?: string;
    groupId?: string;
    resourceRoles?: ResourceRole[];
    enabled?: boolean;
};

export const useListRoleAssignments = ({
    resourceType,
    resourceId,
    groupId,
    resourceRoles,
    enabled = true,
}: ListRoleAssignmentsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();

    return useQuery({
        queryKey: groupsQueryKey.roleAssignments(resourceType, resourceId, groupId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            // Workflows use system-managed groups for sharing, which works on
            // all tiers. Other resource types use custom groups (enterprise only).
            // When filtering by groupId, skip the tier check — the backend allows
            // querying system-managed group assignments (e.g. org-members) on all tiers.
            if (
                resourceType !== ResourceType.WORKFLOW &&
                !groupId &&
                organization?.tier !== OrganizationTier.ENTERPRISE
            ) {
                return {
                    assignments: [],
                };
            }

            // Build filter object with all available options
            const filter: {
                resourceTypes: ResourceType[];
                resourceId?: string;
                groupId?: string;
                resourceRoles?: ResourceRole[];
            } = {
                resourceTypes: [resourceType],
            };

            // Add optional filters
            if (resourceId) {
                filter.resourceId = resourceId;
            }
            if (groupId) {
                filter.groupId = groupId;
            }
            if (resourceRoles && resourceRoles.length > 0) {
                filter.resourceRoles = resourceRoles;
            }

            const assignments = await autoPaginate(
                (params) =>
                    api.groupService.listRoleAssignments({
                        ...params,
                        filter,
                    }),
                {},
                (response) => response.assignments,
                100, // Use maximum page size for faster data fetching
            );

            return {
                assignments: assignments.map(toPlainRoleAssignment),
            };
        },
        enabled: enabled && !!user && !!organization,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type BatchRoleAssignmentOperation =
    | { type: "create"; groupId: string; role: ResourceRole }
    | { type: "update"; assignmentId: string; groupId: string; role: ResourceRole }
    | { type: "delete"; assignmentId: string };

export type BatchUpdateRoleAssignmentsParams = {
    resourceType: ResourceType;
    resourceId: string;
    operations: BatchRoleAssignmentOperation[];
};

export type CreateRoleAssignmentParams = {
    groupId: string;
    resourceType: ResourceType;
    resourceId: string;
    resourceRole: ResourceRole;
};

export const useCreateRoleAssignment = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ groupId, resourceType, resourceId, resourceRole }: CreateRoleAssignmentParams) => {
            const request = new CreateRoleAssignmentRequest({
                groupId,
                resourceType,
                resourceId,
                resourceRole,
            });
            const response = await api.groupService.createRoleAssignment(request);
            return toPlainRoleAssignment(response.assignment!);
        },
        onSuccess: (_assignment, { resourceType, resourceId }) => {
            // Invalidate role assignments for this resource
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.roleAssignments(resourceType, resourceId) });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type DeleteRoleAssignmentParams = {
    assignmentId: string;
    resourceType: ResourceType;
    resourceId: string;
};

export const useDeleteRoleAssignment = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ assignmentId }: DeleteRoleAssignmentParams) => {
            const request = new DeleteRoleAssignmentRequest({
                assignmentId,
            });
            const response = await api.groupService.deleteRoleAssignment(request);
            return response;
        },
        onSuccess: (_response, { resourceType, resourceId }) => {
            // Invalidate role assignments for this resource
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.roleAssignments(resourceType, resourceId) });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useBatchUpdateRoleAssignments = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ resourceType, resourceId, operations }: BatchUpdateRoleAssignmentsParams) => {
            const promises = operations.map((op) => {
                switch (op.type) {
                    case "create":
                        return api.groupService.createRoleAssignment(
                            new CreateRoleAssignmentRequest({
                                groupId: op.groupId,
                                resourceType,
                                resourceId,
                                resourceRole: op.role,
                            }),
                        );
                    case "update":
                        // Role assignments don't support updates, must delete and recreate
                        return api.groupService
                            .deleteRoleAssignment(
                                new DeleteRoleAssignmentRequest({
                                    assignmentId: op.assignmentId,
                                }),
                            )
                            .then(() =>
                                api.groupService.createRoleAssignment(
                                    new CreateRoleAssignmentRequest({
                                        groupId: op.groupId,
                                        resourceType,
                                        resourceId,
                                        resourceRole: op.role,
                                    }),
                                ),
                            );
                    case "delete":
                        return api.groupService.deleteRoleAssignment(
                            new DeleteRoleAssignmentRequest({
                                assignmentId: op.assignmentId,
                            }),
                        );
                }
            });

            await Promise.all(promises);
        },
        onSuccess: (_, { resourceType, resourceId }) => {
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.roleAssignments(resourceType, resourceId) });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

// Configuration for resource types to watch for permission changes
// When adding a new resource type, add it here to enable automatic invalidation

/**
 * Configuration for resource types that should trigger list invalidation
 * when permissions change.
 *
 * To add a new resource type:
 * 1. Add entry to this array with the ResourceType and corresponding query key
 * 2. The self-subscription will automatically detect changes and invalidate
 */
const WATCHED_RESOURCE_TYPES = [
    { type: ResourceType.PROJECT, queryKey: ["projects"] as const },
    { type: ResourceType.RUNNER, queryKey: ["runners"] as const },
] as const;

// Helper functions for query key validation

/**
 * Type guard to check if a value is a valid query key array.
 *
 * @param key - Value to check
 * @returns true if key is a readonly array
 */
function isQueryKeyArray(key: unknown): key is readonly unknown[] {
    return Array.isArray(key);
}

/**
 * Type guard to check if data has the expected permission structure.
 *
 * @param data - Data to check
 * @returns true if data has assignments array
 */
function isPermissionData(data: unknown): data is { assignments: PlainRoleAssignment[] } {
    return (
        typeof data === "object" &&
        data !== null &&
        "assignments" in data &&
        Array.isArray((data as { assignments: unknown }).assignments)
    );
}

/**
 * Compares two query keys for equality.
 *
 * @param key1 - First query key
 * @param key2 - Second query key
 * @returns true if keys are equal
 */
function queryKeysEqual(key1: readonly unknown[], key2: readonly unknown[]): boolean {
    if (key1.length !== key2.length) return false;
    return key1.every((item, index) => item === key2[index]);
}

/**
 * Fetches and caches all resource permissions for the authenticated user.
 *
 * This hook includes a self-subscription mechanism that watches for changes
 * to its own data and selectively invalidates resource lists based on detected
 * permission changes.
 *
 * **IMPORTANT: Single-Instance Pattern**
 * This hook should be called once in a central location (e.g., AuthenticatedUserLayout)
 * to establish the subscription. Multiple instances will create duplicate subscriptions
 * that perform redundant diff computations, though the invalidations remain idempotent.
 * Other components should access the cached data via `useQueryClient().getQueryData()`
 * or rely on resource-specific hooks that consume this data.
 *
 * Returns structured data with:
 * - Resolved permissions (roles -> permissions)
 * - Resource-to-permissions map for O(1) lookups
 * - Resource-to-group-IDs map
 *
 * Prefetched in AuthenticatedUserLayout and consumed by resource-specific hooks.
 *
 * @param enabled - Whether the query should run (default: true)
 * @returns Object containing assignments and computed permission maps
 */
export const useListUserResourcePermissions = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: groupsQueryKey.userResourcePermissions(),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            // Return empty permissions for non-Enterprise organizations
            if (organization?.tier !== OrganizationTier.ENTERPRISE) {
                const emptyComputed = computeResourcePermissions([]);
                return {
                    assignments: [],
                    ...emptyComputed,
                };
            }

            const filter = {
                userId: user.id,
                resourceTypes: [
                    ResourceType.PROJECT,
                    ResourceType.RUNNER,
                    ResourceType.ORGANIZATION,
                    ResourceType.WORKFLOW,
                    ResourceType.GROUP,
                ],
            };

            const assignments = await autoPaginate(
                (params) =>
                    api.groupService.listRoleAssignments({
                        ...params,
                        filter,
                    }),
                {},
                (response) => response.assignments,
                100, // Use maximum page size for faster data fetching
            );

            const plainAssignments = assignments.map(toPlainRoleAssignment);

            // Compute resource permissions (resolves roles to permissions)
            const computed = computeResourcePermissions(plainAssignments);

            return {
                assignments: plainAssignments,
                ...computed,
            };
        },
        enabled: enabled && !!user && !!organization,
        staleTime: 15 * 60_000, // 15 minutes - permissions don't change frequently
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });

    // Use ref to store previous data across renders and remounts
    const previousDataRef = useRef<{ assignments: PlainRoleAssignment[] }>();

    // Self-subscription: watch for changes to THIS query and invalidate resource lists
    useEffect(() => {
        // Only set up subscription if query is enabled
        if (!enabled || !user) {
            return;
        }

        const thisQueryKey = groupsQueryKey.userResourcePermissions();

        const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
            // Only process 'updated' events
            if (event.type !== "updated") return;

            const eventQuery = event.query;

            // Check if this event is for THIS query
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const eventQueryKey = eventQuery.queryKey;
            if (!isQueryKeyArray(eventQueryKey) || !queryKeysEqual(eventQueryKey, thisQueryKey)) {
                return;
            }

            // Get current data with type guard
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const newData = eventQuery.state.data;
            if (!isPermissionData(newData)) {
                return;
            }

            // Need both old and new data to compute diff
            const previousData = previousDataRef.current;
            if (!previousData) {
                // Store current data for next comparison
                previousDataRef.current = newData;
                return;
            }

            // Check each configured resource type for changes and invalidate if needed
            WATCHED_RESOURCE_TYPES.forEach(({ type, queryKey }) => {
                const hasChanges = hasResourceTypeChanges(previousData.assignments, newData.assignments, type);

                if (hasChanges) {
                    queryClient
                        .invalidateQueries({
                            queryKey: keyWithPrincipal(queryKey),
                        })
                        .catch((error) => {
                            console.error(`Failed to invalidate ${queryKey.join("/")} after permission change:`, error);
                        });
                }
            });

            // Store current data for next comparison
            previousDataRef.current = newData;
        });

        return () => {
            unsubscribe();
        };
    }, [enabled, user, queryClient]);

    return query;
};

// Event handlers for permission change events

/**
 * Handle GROUP_MEMBERSHIP_CHANGED events from WatchEvents stream.
 *
 * Invalidates all group-related caches. The permission query's self-subscription
 * will automatically detect changes and invalidate affected resource lists.
 */
export async function handleGroupMembershipEvent(client: QueryClient): Promise<void> {
    // Invalidate all group-related queries
    // This includes permissions, which triggers the self-subscription diff logic
    await client.invalidateQueries({ queryKey: keyWithPrincipal(["groups"]) });

    // Invalidate per-resource permission queries (dataloader path)
    await client.invalidateQueries({ queryKey: keyWithPrincipal(["permissions"]) });
}

/**
 * Handle ROLE_ASSIGNMENT_CHANGED events from WatchEvents stream.
 *
 * Invalidates role assignment and permission caches. The permission query's
 * self-subscription will automatically detect changes and invalidate affected
 * resource lists.
 */
export async function handleRoleAssignmentEvent(client: QueryClient): Promise<void> {
    // Invalidate role assignment queries
    await client.invalidateQueries({
        queryKey: keyWithPrincipal(["groups", "roleAssignments"]),
    });

    // Invalidate user resource permissions
    // The permission query's self-subscription will detect the change and
    // selectively invalidate affected resource lists (projects, runners, etc.)
    await client.invalidateQueries({
        queryKey: keyWithPrincipal(["groups", "resourcePermissions"]),
    });

    // Invalidate per-resource permission queries (dataloader path)
    await client.invalidateQueries({ queryKey: keyWithPrincipal(["permissions"]) });
}

// Direct share hooks for sharing resources with individual users/service accounts

export type ShareResourceWithPrincipalParams = {
    resourceType: ResourceType;
    resourceId: string;
    principal: Principal;
    principalId: string;
    role: ResourceRole;
};

export const useShareResourceWithPrincipal = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            resourceType,
            resourceId,
            principal,
            principalId,
            role,
        }: ShareResourceWithPrincipalParams) => {
            const request = new ShareResourceWithPrincipalRequest({
                resourceType,
                resourceId,
                principal,
                principalId,
                role,
            });
            await api.groupService.shareResourceWithPrincipal(request);
        },
        onSuccess: (_, { resourceType, resourceId }) => {
            // Invalidate role assignments for this resource to refresh the list
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.roleAssignments(resourceType, resourceId) });
            // Invalidate groups list as new direct share groups may have been created
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.list().slice(0, -1) });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type UnshareResourceWithPrincipalParams = {
    resourceType: ResourceType;
    resourceId: string;
    principal: Principal;
    principalId: string;
};

export const useUnshareResourceWithPrincipal = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            resourceType,
            resourceId,
            principal,
            principalId,
        }: UnshareResourceWithPrincipalParams) => {
            const request = new UnshareResourceWithPrincipalRequest({
                resourceType,
                resourceId,
                principal,
                principalId,
            });
            await api.groupService.unshareResourceWithPrincipal(request);
        },
        onSuccess: (_, { resourceType, resourceId }) => {
            // Invalidate role assignments for this resource to refresh the list
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.roleAssignments(resourceType, resourceId) });
            // Invalidate groups list as direct share groups may have been modified
            void queryClient.invalidateQueries({ queryKey: groupsQueryKey.list().slice(0, -1) });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};
